import React, { useEffect, useMemo, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetAllocatedProperties } from '~/hooks/apis/connection-google/useGetAllocatedProperties';
import { useGetPropertyHintCoverage } from '~/hooks/apis/connection-google/useGetPropertyHintCoverage';

import {
  AllocatedProperty,
  HintCoverageAccountType,
  PropertyHintCoverageRow,
} from '~/services/ConnectionGoogleService';
import { toDayJs } from '~/services/TimeService';

import FreshnessDistributionBar from './FreshnessDistributionBar';
import PropertyHintCoverageCalendar from './PropertyHintCoverageCalendar';
import {
  calendarPageForDate,
  coverageCellKey,
  derivePropertyCoverage,
  formatAge,
  hintDay,
} from './propertyCoverageUtils';

interface Props {
  propertyId: string;
  accountType: HintCoverageAccountType;
  /** Row data from the Allocated Properties grid when opened via row click; null on deep link. */
  initialProperty: AllocatedProperty | null;
  onBack: () => void;
  /** Navigate to another property in place — e.g. pasting the next Hotel ID from a GHA report. */
  onPropertyIdSubmit: (propertyId: string) => void;
}

interface SummaryCardProps {
  label: string;
  value: string;
  caption?: React.ReactNode;
}

function SummaryCard({ label, value, caption }: SummaryCardProps) {
  return (
    <Paper sx={{ p: 2, flex: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" mt={0.5}>
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Paper>
  );
}

export default function PropertyHintCoverageView({
  propertyId,
  accountType,
  initialProperty,
  onBack,
  onPropertyIdSubmit,
}: Props) {
  const theme = useTheme();
  // Snapshot "now" once per mount so ages, bands, and the calendar's today anchor are consistent
  // across the whole view for a given render of the data.
  const [now] = useState(() => new Date());
  const [calendarPage, setCalendarPage] = useState(0);
  const [findCheckIn, setFindCheckIn] = useState('');
  const [findNights, setFindNights] = useState('');
  const [propertyIdInput, setPropertyIdInput] = useState(propertyId);

  const submitPropertyId = () => {
    const trimmed = propertyIdInput.trim();
    if (trimmed && trimmed !== propertyId) {
      onPropertyIdSubmit(trimmed);
    }
  };

  const { data, isLoading, isError } = useGetPropertyHintCoverage(propertyId, accountType);

  // Header rehydration on deep link/refresh: reuse the allocated-properties UUID search rather
  // than adding any backend. If the property is not classified (deep-linked from a GHA report),
  // the header falls back to the bare UUID and coverage still renders.
  const { data: lookupData } = useGetAllocatedProperties(
    initialProperty ? null : { accountType, search: propertyId, page: 1, limit: 1 },
  );
  const property = initialProperty ?? lookupData?.data?.[0] ?? null;

  const derived = useMemo(() => derivePropertyCoverage(data?.rows ?? [], now), [data?.rows, now]);

  useEffect(() => {
    setCalendarPage(0);
    setFindCheckIn('');
    setFindNights('');
  }, [propertyId, accountType]);

  // Keep the input in sync when the property changes from outside (row click, back/forward).
  useEffect(() => {
    setPropertyIdInput(propertyId);
  }, [propertyId]);

  const highlightKey = useMemo(() => {
    if (!findCheckIn || !findNights) {
      return null;
    }
    return coverageCellKey(findCheckIn, Number(findNights));
  }, [findCheckIn, findNights]);

  // Page the calendar to the searched check-in date so the highlighted cell is visible.
  useEffect(() => {
    if (findCheckIn) {
      setCalendarPage(calendarPageForDate(findCheckIn, toDayJs(now)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findCheckIn]);

  const filteredRows = useMemo(() => {
    const rows = data?.rows ?? [];
    return rows.filter((row) => {
      if (findCheckIn && row.checkInDate !== findCheckIn) {
        return false;
      }
      if (findNights && row.los !== Number(findNights)) {
        return false;
      }
      return true;
    });
  }, [data?.rows, findCheckIn, findNights]);

  const columns: Array<GridColDef<PropertyHintCoverageRow>> = useMemo(
    () => [
      { field: 'checkInDate', headerName: 'Check-in', width: 130 },
      { field: 'los', headerName: 'LOS', width: 80 },
      { field: 'abw', headerName: 'ABW at serve', width: 120 },
      {
        // The account-timezone day the hint counted from (checkInDate − ABW). Disambiguates rows
        // whose local served_at date looks identical but whose serve fell on different UTC days.
        field: 'hintDay',
        headerName: 'Hint day',
        width: 120,
        valueGetter: (_value, row) => hintDay(row),
      },
      { field: 'tier', headerName: 'Tier', width: 100 },
      {
        field: 'servedAt',
        headerName: 'Last hinted',
        width: 200,
        // valueFormatter (not valueGetter) so sorting compares the raw ISO timestamp, not the
        // locale display string.
        valueFormatter: (value: string) => new Date(value).toLocaleString(),
      },
      {
        field: 'age',
        headerName: 'Price freshness',
        width: 140,
        sortable: false,
        valueGetter: (_value, row) => formatAge(row.servedAt, now),
      },
    ],
    [now],
  );

  return (
    <Box>
      <PageSubheader title={property ? `${property.name}` : `Property ${propertyId}`}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back to coverage
        </Button>
      </PageSubheader>

      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <TextField
          size="small"
          label="Property ID"
          value={propertyIdInput}
          onChange={(e) => setPropertyIdInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              submitPropertyId();
            }
          }}
          // Wide enough for a full UUID in the monospace face, so IDs are still scannable.
          sx={{ width: theme.spacing(52), '& input': { fontFamily: 'monospace', fontSize: 13 } }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Open property" onClick={submitPropertyId}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Chip size="small" label={accountType.toUpperCase()} />
        {property && (
          <>
            <Chip size="small" label={`Tier ${property.tier}`} />
            {property.goCountry && <Chip size="small" label={property.goCountry} />}
            {property.isForced && <Chip size="small" color="warning" label="Forced" />}
            {property.opportunityCount !== null && (
              <Chip size="small" label={`Opportunities ${property.opportunityCount}`} />
            )}
          </>
        )}
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      )}

      {isError && <Alert severity="error">Failed to load property hint coverage</Alert>}

      {data?.rows.length === 0 && (
        <Alert severity="info">
          No hint coverage for this property under {accountType.toUpperCase()}. Try another account type.
        </Alert>
      )}

      {data && data.rows.length > 0 && (
        <>
          <Stack direction="row" spacing={2} mb={2}>
            <SummaryCard
              label="Itineraries hinted"
              value={derived.itineraryCount.toLocaleString()}
              caption="distinct check-in × LOS"
            />
            <SummaryCard
              label="Next 28 days coverage"
              value={`${derived.next28Days.pct}%`}
              caption={`${derived.next28Days.covered.toLocaleString()} / ${derived.next28Days.total.toLocaleString()} cells`}
            />
            <SummaryCard
              label="Hinted within 48h"
              value={`${derived.freshWithin48h.pct}%`}
              caption={`${derived.freshWithin48h.count.toLocaleString()} of ${derived.itineraryCount.toLocaleString()} itineraries`}
            />
            <SummaryCard
              label="Last hint served"
              value={derived.lastHintServedAt ? `${formatAge(derived.lastHintServedAt, now)} ago` : '—'}
              caption={derived.lastHintServedAt ? new Date(derived.lastHintServedAt).toLocaleString() : undefined}
            />
            <SummaryCard
              label="Oldest hint served"
              value={derived.oldestHint ? `${formatAge(derived.oldestHint.servedAt, now)} ago` : '—'}
              caption={
                derived.oldestHint ? (
                  <>
                    check-in {derived.oldestHint.checkInDate}
                    <Box component="span" display="block">
                      hint served {new Date(derived.oldestHint.servedAt).toLocaleString()}
                    </Box>
                  </>
                ) : undefined
              }
            />
          </Stack>

          <FreshnessDistributionBar freshness={derived.freshness} itineraryCount={derived.itineraryCount} />

          <PropertyHintCoverageCalendar
            cellMap={derived.cellMap}
            losValues={derived.losValues}
            page={calendarPage}
            onPageChange={setCalendarPage}
            highlightKey={highlightKey}
            now={now}
          />

          <Box mt={4}>
            <Typography variant="h6" mb={1}>
              Itineraries
            </Typography>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <DatePicker
                    label="Check-in date"
                    value={findCheckIn ? toDayJs(findCheckIn) : null}
                    onChange={(value) => setFindCheckIn(value?.isValid() ? value.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Nights (LOS)"
                    type="number"
                    value={findNights}
                    onChange={(e) => setFindNights(e.target.value)}
                    slotProps={{ htmlInput: { min: 1 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 2 }}>
                  <Stack height="100%" justifyContent="center">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFindCheckIn('');
                        setFindNights('');
                      }}
                    >
                      Clear
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            <DataGrid
              rows={filteredRows}
              columns={columns}
              getRowId={(row) => `${row.abw}:${row.los}`}
              initialState={{
                // Freshest serve first (lowest age), mirroring the backend's default order.
                sorting: { sortModel: [{ field: 'servedAt', sort: 'desc' }] },
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              slots={{ pagination: GridPagination }}
              disableRowSelectionOnClick
            />
          </Box>
        </>
      )}
    </Box>
  );
}
